import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FaIcon, FloatingPanel, NgButton, NgInlineActionError, PanelErrorWithIcon, PanelInfoWithIcon } from '@/ui';
import type { FloatingPanelState } from '@/ui';
import { LiveVideoSource } from '@/signalr';
import type { LiveVideo } from '@/signalr';
import { useIncidentRealtime } from '@ngsos/portal-core/chat';
import { requestAppleEedVideo } from '@ngsos/portal-core/commands';
import { useVideoCall } from './useVideoCall';
import { MediasoupVideoViewer } from './MediasoupVideoViewer';
import { VideoBoxLayout } from './VideoBoxLayout';
import { VideoStatsLink } from './VideoStatsLink';
import {
  canSwapCameraOrTakeSnapshot,
  canToggleTorch,
  getDisplayPanelMode,
  getWebAppVideoPreparation,
  getWebAppStateDescriptionKey,
  getWebAppStateIcon,
  getWebAppStateTextKey,
  isRewindDisabled,
} from '@ngsos/portal-core/video';
import type { DisplayPanelMode, VideoCallState } from '@ngsos/portal-core/video';
import { AppleEedVideoPreparation, GoogleElsVideoPreparation, WebAppVideoPreparation } from '@ngsos/portal-ui';
import appleEedShareImage from '../../../../../packages/ui/src/assets/video-guidance/apple-eed-share-video.png';
import webAppSmsImage from '../../../../../packages/ui/src/assets/video-guidance/webapp-sms-link.png';
import webAppCameraImage from '../../../../../packages/ui/src/assets/video-guidance/webapp-allow-camera.png';
import webAppLocationImage from '../../../../../packages/ui/src/assets/video-guidance/webapp-allow-location.png';
import googleElsShareImage from '../../../../../packages/ui/src/assets/video-guidance/android-share-video.png';
import googleElsSpeakerImage from '../../../../../packages/ui/src/assets/video-guidance/android-enable-speaker.png';

export type MediasoupVideoBoxProps = {
  incidentId: string;
  liveVideo: LiveVideo;
  videoExpanded: boolean;
  onVideoExpandedChanged: (expanded: boolean) => void;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId?: string | null;
};

const FLOATING_STATE_CSS: Record<FloatingPanelState, string> = {
  docked: 'mediasoup-video-box--floating-state-docked',
  floating: 'mediasoup-video-box--floating-state-floating',
  fullscreen: 'mediasoup-video-box--floating-state-fullscreen',
};

export function MediasoupVideoBox({
  incidentId,
  liveVideo,
  videoExpanded,
  onVideoExpandedChanged,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
}: MediasoupVideoBoxProps) {
  void incidentId;
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();

  const resendAppleEedVideoRequest = async () => {
    if (appleEedVideoCallId == null) {
      return;
    }
    await requestAppleEedVideo(appleEedVideoCallId, liveVideo.id);
  };

  const {
    state,
    videoServerUrl,
    toggleRecordingAndPauseOrResumeStream,
    switchUserCamera,
    toggleUserTorch,
    switchCameraPending,
    toggleTorchPending,
    takeSnapshot,
    endLiveVideo,
  } =
    useVideoCall({ liveVideo, realtime });

  const [floatingPanelState, setFloatingPanelState] = useState<FloatingPanelState>('docked');

  const isAppleEedVideo = liveVideo.source === LiveVideoSource.AppleEed;
  const appleEedCssClass = isAppleEedVideo ? 'mediasoup-video-box--eed' : '';
  const videoExpandedCssClass = videoExpanded ? 'mediasoup-video-box--expanded' : '';
  const floatingStateCssClass = FLOATING_STATE_CSS[floatingPanelState];

  const { showRecording } = state;
  const hideControls = showRecording;
  const liveIndicatorCssClass = showRecording
    ? 'mediasoup-video-box__live--inactive'
    : 'mediasoup-video-box__live--active';
  const liveStreamLabel =
    liveVideo.source === LiveVideoSource.AppleEed
      ? t('Incident_Video_AppleEedLiveStream')
      : liveVideo.source === LiveVideoSource.GoogleEls
        ? t('Incident_Video_GoogleElsLiveStream')
        : t('Incident_Video_WebAppLiveStream');

  const currentDisplayMode = getDisplayPanelMode(state, liveVideo.source);
  const preparationCssClass = currentDisplayMode === 'GoogleElsInstructions' || currentDisplayMode === 'AppleEedInstructions' || (liveVideo.source === 'WebApp' && getWebAppVideoPreparation(currentDisplayMode, state.webAppState) !== null) ? 'mediasoup-video-box--preparation' : '';

  const onVideoBoxClicked = () => {
    onVideoExpandedChanged(!videoExpanded);
  };

  const webAppStateText = t(getWebAppStateTextKey(state.webAppState));
  const webAppStateDescriptionKey = getWebAppStateDescriptionKey(state.webAppState);
  const webAppStateDescription = webAppStateDescriptionKey !== null ? t(webAppStateDescriptionKey) : '';

  return (
    <FloatingPanel
      state={floatingPanelState}
      onStateChanged={setFloatingPanelState}
      floatingDisabledOnMobile
      disableFloating
      disableFullscreen
      floatingDisabled
    >
      <div
        className={`mediasoup-video-box ${floatingStateCssClass} ${appleEedCssClass} ${videoExpandedCssClass} ${preparationCssClass}`}
        onClick={onVideoBoxClicked}
        data-testid="incident-video-box"
      >
        <VideoStatsLink
          videoId={liveVideo.id}
          videoServerUrl={videoServerUrl}
          className="video-stats-link mediasoup-video-box__stats-link"
        />
        <button
          className="mediasoup-video-box__expand-btn"
          data-testid="incident-video-expand-button"
          onClick={(event) => {
            event.stopPropagation();
            onVideoBoxClicked();
          }}
          title={t('Incident_Video_ExpandVideo')}
        >
          <FaIcon icon={videoExpanded ? 'fal fa-compress' : 'fal fa-expand'} />
        </button>

        <VideoBoxLayoutContent
          state={state}
          isAppleEedVideo={isAppleEedVideo}
          isWebAppVideo={liveVideo.source === 'WebApp'}
          currentDisplayMode={currentDisplayMode}
          hideControls={hideControls}
          liveIndicatorCssClass={liveIndicatorCssClass}
          liveStreamLabel={liveStreamLabel}
          webAppStateText={webAppStateText}
          webAppStateDescription={webAppStateDescription}
          allowAppleEedVideoActions={allowAppleEedVideoActions}
          appleEedVideoCallId={appleEedVideoCallId ?? null}
          onResendAppleEedVideoRequest={resendAppleEedVideoRequest}
          toggleRecordingAndPauseOrResumeStream={toggleRecordingAndPauseOrResumeStream}
          switchUserCamera={switchUserCamera}
          toggleUserTorch={toggleUserTorch}
          switchCameraPending={switchCameraPending}
          toggleTorchPending={toggleTorchPending}
          takeSnapshot={takeSnapshot}
          endLiveVideo={endLiveVideo}
        />
      </div>
    </FloatingPanel>
  );
}

type VideoBoxLayoutContentProps = {
  state: VideoCallState;
  isAppleEedVideo: boolean;
  isWebAppVideo?: boolean;
  currentDisplayMode: DisplayPanelMode;
  hideControls: boolean;
  liveIndicatorCssClass: string;
  liveStreamLabel: string;
  webAppStateText: string;
  webAppStateDescription: string;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId: string | null;
  onResendAppleEedVideoRequest: () => Promise<void>;
  toggleRecordingAndPauseOrResumeStream: () => Promise<void>;
  switchUserCamera: () => Promise<void>;
  toggleUserTorch: () => Promise<void>;
  switchCameraPending: boolean;
  toggleTorchPending: boolean;
  takeSnapshot: () => Promise<void>;
  endLiveVideo: () => Promise<void>;
};

export function VideoBoxLayoutContent({
  state,
  isAppleEedVideo,
  isWebAppVideo = false,
  currentDisplayMode,
  hideControls,
  liveIndicatorCssClass,
  liveStreamLabel,
  webAppStateText,
  webAppStateDescription,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
  onResendAppleEedVideoRequest,
  toggleRecordingAndPauseOrResumeStream,
  switchUserCamera,
  toggleUserTorch,
  switchCameraPending,
  toggleTorchPending,
  takeSnapshot,
  endLiveVideo,
}: VideoBoxLayoutContentProps) {
  const { t } = useTranslation();
  const { showRecording } = state;
  const webAppPreparation = isWebAppVideo ? getWebAppVideoPreparation(currentDisplayMode, state.webAppState) : null;

  return (
    <VideoBoxLayout
      contentTemplate={
        <>
          {state.callerConnectionState === 'Connected' && (
            <p className={liveIndicatorCssClass} data-testid="incident-video-live-badge">
              {showRecording ? t('Incident_Video_RecordingPlaying') : liveStreamLabel}
            </p>
          )}

          <div
            className={`mediasoup-video-box__viewer-wrapper ${
              currentDisplayMode === 'LiveVideo' || currentDisplayMode === 'RecordingPlayback' ? '' : 'd-none'
            }`}
          >
            <MediasoupVideoViewer
              showRecording={showRecording}
              connectionState={state.callerConnectionState}
              cameraState={state.cameraState}
            />
          </div>

          {currentDisplayMode === 'FatalError' && (
            <div className="pb-4 text-center">
              <div className="mx-3">
                <PanelErrorWithIcon
                  title={webAppStateText}
                  description={webAppStateDescription}
                  centerContent
                  testID="incident-video-error-panel"
                />
              </div>
            </div>
          )}

          {currentDisplayMode === 'AppleEedInstructions' && (
            <div className="mediasoup-video-box__preparation" onClick={(event) => event.stopPropagation()}>
              <AppleEedVideoPreparation
                title={t('Incident_Video_AppleEedPreparation_Request')}
                shareLabel={t('Incident_Video_AppleEedPreparation_Share')}
                shareImage={{ uri: appleEedShareImage }}
                footer={allowAppleEedVideoActions && appleEedVideoCallId != null ? (
                  <NgButton
                    text={t('Incident_Button_ResendVideoRequest')}
                    icon="fal fa-video-plus"
                    size="small"
                    showProgress
                    onClick={onResendAppleEedVideoRequest}
                  />
                ) : null}
              />
            </div>
          )}

          {currentDisplayMode === 'Disconnected' && (
            <PanelInfoWithIcon>
              <img src="Images/Incident/video-canceled.svg" alt="Video disconnected" />
              <div data-testid="incident-video-disconnected-text">{t('Incident_Video_VideoStreamCancelled')}</div>
              <div>{t('Incident_Video_WaitingForCameraAccessNote')}</div>
            </PanelInfoWithIcon>
          )}

          {currentDisplayMode === 'Connecting' && webAppPreparation === null && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Connecting" />
                <div data-testid="incident-video-status-text">{t('Incident_Video_ConnectingToCall')}</div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'WebAppState' && webAppPreparation === null && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Video status" />
                <div data-testid="incident-video-status-text">{webAppStateText}</div>
                <div className="small text-black-50" data-testid="incident-video-status-description-text">
                  {webAppStateDescription}
                </div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'GoogleElsInstructions' && (
            <div className="mediasoup-video-box__preparation" onClick={(event) => event.stopPropagation()}>
              <GoogleElsVideoPreparation
                title={t('Incident_Video_GoogleElsPreparation_Title')}
                shareLabel={t('Incident_Video_GoogleElsPreparation_Share')}
                speakerLabel={t('Incident_Video_GoogleElsPreparation_Speaker')}
                shareImage={{ uri: googleElsShareImage }}
                speakerImage={{ uri: googleElsSpeakerImage }}
              />
            </div>
          )}

          {webAppPreparation !== null && (
            <div className="mediasoup-video-box__preparation" onClick={(event) => event.stopPropagation()}>
              <WebAppVideoPreparation
                phase={webAppPreparation}
                title={t(webAppPreparation === 'sms' ? 'Incident_Video_WebAppPreparation_Sms' : 'Incident_Video_WebAppPreparation_Camera')}
                labels={{ sms: t('Incident_Video_WebAppPreparation_Sms'), camera: t('Incident_Video_WebAppPreparation_Camera'), location: t('Incident_Video_WebAppPreparation_Location') }}
                images={{ sms: { uri: webAppSmsImage }, camera: { uri: webAppCameraImage }, location: { uri: webAppLocationImage } }}
              />
            </div>
          )}

          {currentDisplayMode === 'CameraTurnedOff' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src="Images/Incident/video-canceled.svg" alt="Camera disabled" />
                <div data-testid="incident-video-camera-off-text">{t('Incident_Video_CameraDisabled')}</div>
                <div>{t('Incident_Video_CameraDisabledByUser')}</div>
              </PanelInfoWithIcon>
            </div>
          )}
        </>
      }
      actionsTemplate={
        <div onClick={(event) => event.stopPropagation()}>
          {!hideControls ? (
            <>
              <div className="btn-group">
                <NgButton
                  onClick={toggleRecordingAndPauseOrResumeStream}
                  type="dark"
                  disabled={isRewindDisabled(state)}
                  testID="incident-video-rewind-button"
                >
                  -5s
                </NgButton>
                {!isAppleEedVideo && (
                  <>
                    <NgInlineActionError
                      message={t('Error_SwitchCameraFailed')}
                      trigger={state.lastCameraSwitchFailedAt}
                    >
                      <NgButton
                        icon="fal fa-camera-rotate"
                        title={t('Button_SwapCamera')}
                        disabled={!canSwapCameraOrTakeSnapshot(state) || switchCameraPending}
                        outline
                        testID="incident-video-switch-camera-button"
                        onClick={switchUserCamera}
                      />
                    </NgInlineActionError>

                    <NgInlineActionError
                      message={t('Error_ToggleTorchFailed')}
                      trigger={state.lastTorchToggleFailedAt}
                    >
                      <NgButton
                        icon={state.torchOn ? 'fal fa-lightbulb-on' : 'fal fa-lightbulb'}
                        title={state.torchOn ? t('Button_TurnOffTorch') : t('Button_TurnOnTorch')}
                        disabled={!canToggleTorch(state) || toggleTorchPending}
                        outline
                        testID="incident-video-torch-button"
                        onClick={toggleUserTorch}
                      />
                    </NgInlineActionError>

                    <NgButton
                      icon="fal fa-camera"
                      title={t('Button_TakeSnapshot')}
                      disabled={!canSwapCameraOrTakeSnapshot(state)}
                      outline
                      testID="incident-video-snapshot-button"
                      onClick={takeSnapshot}
                    />
                  </>
                )}
              </div>

              {!isAppleEedVideo && (
                <span onClick={(event) => event.stopPropagation()}>
                  <NgButton
                    icon="fal fa-video-slash"
                    title={t('Incident_Button_StopVideoStream')}
                    type="danger"
                    testID="incident-video-stop-button"
                    onClick={endLiveVideo}
                  />
                </span>
              )}
            </>
          ) : (
            <div className="mediasoup-video-box__go-live">
              <NgButton
                onClick={toggleRecordingAndPauseOrResumeStream}
                size="small"
                type="dark"
                testID="incident-video-go-live-button"
              >
                <span>{t('Incident_Button_PlayLiveStream')}</span>
              </NgButton>
            </div>
          )}
        </div>
      }
    />
  );
}
